import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import api from "../lib/axios";
import type { Store } from "../types/store";
import type { RootState } from "../store";


//--------------------------------------------------
// THUNKS
//--------------------------------------------------

// GET ALL STORES
export const getStores = createAsyncThunk(
  "store/all",
  async (_, thunkAPI) => {
    try {
      const res = await api.get("/store/all");
      return res.data.data as Store[];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || "Failed to fetch stores");
    }
  }
);

// CREATE STORE
export const createStore = createAsyncThunk(
  "store/create",
  async (
    payload: {  
      name: string;
      location: string;
      contactInfo: string;
      managerId?: string;
      businessId?: string; },
    thunkAPI
  ) => {
    try {
      // console.log("Store data: ", payload);
      const res = await api.post("/store/add", payload);
      return res.data.data as Store; // assumes API returns created store
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || "Failed to create store");
    }
  }
);

// Thunk for updating a store
export const updateStore = createAsyncThunk<
  Store, // Return type (the updated store)
  { id: string; data: any }, // Argument type (id and update data)
  { rejectValue: string } // Error type
>(
  "stores/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/store/update/${id}`, data);
      return res.data.data as Store; // Controller returns { data: updatedStore }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to update store");
    }
  }
);


// Thunk for deleting stores
export const deleteStores = createAsyncThunk(
  "stores/delete",
  async (store_ids: string[], { rejectWithValue }) => {
    try {
      const res = await api.put("/store/delete", { store_ids }); 
      return res.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete stores");
    }
  }
);

//--------------------------------------------------
// SLICE
//--------------------------------------------------


interface StoreState {
  stores: Store[];
  adding: boolean;
  loading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: StoreState = {
  stores: [],
  loading: false,
  adding: false,
  error: null,
  success: false,
};

const storeSlice = createSlice({
  name: "store",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // GET STORES
    builder.addCase(getStores.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getStores.fulfilled, (state, action: PayloadAction<Store[]>) => {
      state.loading = false;
      // console.log("Loading? ", state.loading);
      state.stores = action.payload ?? [];
      state.success = true;
    });
    builder.addCase(getStores.rejected, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // CREATE STORE
    builder.addCase(createStore.pending, (state) => {
      state.error = null;
      state.adding = true;
    });
    builder.addCase(createStore.fulfilled, (state, action: PayloadAction<Store>) => {
      state.adding = false;
      // console.log("Store: ", action.payload);
      state.stores.unshift(action.payload);
    });
    builder.addCase(createStore.rejected, (state, action: PayloadAction<any>) => {
      state.adding = false;
      state.error = action.payload;
    });
    
    // UPDATE STORE
    builder.addCase(updateStore.pending, (state) => {
      state.error = null;
      state.adding = true;
      // state.loading = true;
    })
    .addCase(updateStore.rejected, (state, action: PayloadAction<any>) => {
      // state.loading = false;
      state.adding = false;
      state.error = action.payload;
    })
    .addCase(updateStore.fulfilled, (state, action: PayloadAction<any>) => {
      state.adding = false;
      // console.log("Store: ", action.payload);
      const idx = state.stores.findIndex((store) => store.id === action.payload.id);
      if(idx !== -1) state.stores[idx] = action.payload;
    });

    // DELETE STORE
    builder.addCase(deleteStores.pending, (state) => {
      // state.loading = true;
      state.error = null;
    })
    .addCase(deleteStores.fulfilled, (state, action) => {
      state.loading = false;
      state.stores = state.stores.filter(
          (store) => !action.payload.data.includes(store.id)
      );
    })
    .addCase(deleteStores.rejected, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload.message || "Failed to delete stores";
    });
  },
});

export const selectStores = (state: RootState) => state.stores.stores;
export const selectStoreLoading = (state: RootState) => state.stores.loading;
export const selectStoreError = (state: RootState) => state.stores.error;

export const storeReducer = storeSlice.reducer;
